
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Memory } from "@/types/memory";

interface RecategorizeMemoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (memoryId: number, newCategory: string) => void;
  memory: Memory | null;
}

const categories = [
  { value: "travel", label: "Travel" },
  { value: "health", label: "Health" },
  { value: "meals", label: "Meals" },
  { value: "errands", label: "Errands" },
  { value: "home", label: "Home" },
  { value: "work", label: "Work" },
];

const RecategorizeMemoryDialog = ({
  open,
  onOpenChange,
  onConfirm,
  memory
}: RecategorizeMemoryDialogProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  const handleConfirm = () => {
    if (memory && selectedCategory) {
      onConfirm(memory.id, selectedCategory);
      setSelectedCategory("");
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedCategory("");
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-soft-slate border-graphite">
        <DialogHeader>
          <DialogTitle className="text-foreground">Re-categorize Memory</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Select a new category for this memory.
          </DialogDescription>
        </DialogHeader>
        
        {memory && (
          <div className="py-4">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Memory:</p>
              <p className="text-foreground text-sm bg-white/50 p-3 rounded border border-border">
                {memory.memory}
              </p>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Current category:</p>
              <p className="text-foreground text-sm capitalize">{memory.category}</p>
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground mb-2">New category:</p>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full bg-white border-border text-foreground">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-white border-border">
                  {categories
                    .filter(cat => cat.value !== memory.category)
                    .map((category) => (
                      <SelectItem 
                        key={category.value} 
                        value={category.value}
                        className="text-foreground hover:bg-muted focus:bg-muted"
                      >
                        {category.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => handleOpenChange(false)}
            className="border-border text-muted-foreground hover:bg-muted"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!selectedCategory}
            className="bg-primary hover:bg-primary/80 text-primary-foreground"
          >
            Update Category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecategorizeMemoryDialog;
